/**
 * Key-normalisation and cache-busting helpers shared by every asset tile.
 * Extracted from the old monolithic Production page so the galleries, the review
 * gates and the work queue all match ids the same way.
 */

/**
 * Normalized key for matching a Work Queue target (may be a display name like
 * "Research Commons") to a tile id (file name like "RESEARCH_COMMONS").
 */
export const normKey = (s: string): string => s.toUpperCase().replace(/\s+/g, '_').replace(/\./g, '').replace(/\//g, '_');

/**
 * Entity-identity key mirroring the engine's canonical_entity_key: fold EVERY
 * punctuation/separator run to one underscore and drop a leading article, so an
 * asset stored as "Abuela Espina's Crypt-Parlor" matches the manifest's canonical
 * "ABUELA_ESPINA_S_CRYPT_PARLOR". normKey leaves apostrophes/hyphens intact, which
 * silently broke location/prop reference-strip lookups.
 */
export const canonKey = (s: string): string => {
  let k = (s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
  for (const art of ['THE ', 'A ', 'AN ']) {
    if (k.startsWith(art)) {
      k = k.slice(art.length).trim();
      break;
    }
  }
  return k.replace(/ /g, '_');
};

/**
 * Best-of-N candidate intermediates surface as `<type>/candidates/<NAME>_cN` tiles
 * alongside the promoted winner. Once the winner exists the pick is made, so these
 * are greyed out and lose the ITERATING chip. Returns the parent design's name (for
 * matching against the promoted tile) or null for a non-candidate.
 */
export const candidateParent = (shotId: string): string | null => {
  const m = /(?:^|\/)candidates\/(.+?)_c\d+$/i.exec(shotId);
  return m ? m[1] : null;
};

/**
 * Append a rework-generation counter so a tile refetches its image after a rework
 * completes (the base `?v=` is frozen at discovery time).
 */
export const bustSrc = (src: string, n?: number): string => (n ? `${src}${src.includes('?') ? '&' : '?'}rb=${n}` : src);

/** Grid tiles load a server-generated ~512px JPEG; the lightbox keeps full-res. */
export const thumbSrc = (src: string): string => `${src}${src.includes('?') ? '&' : '?'}thumb=1`;

/** Character sheets are stored per-view; these suffixes collapse under the model sheet. */
export const VIEW_SUFFIX_RE = /_(front|profile|threequarter|three_quarter|3q|side|faceclose|hairdetail)$/i;
